import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { API } from "../auth/AuthContext";
import ProfileLayout from "../components/ProfileLayout";
import { formatLocalTimestamp } from "../lib/dateLocal";
import { pageItems } from "../lib/pageItems";
import { LifeBuoy } from "lucide-react";

const STATUS_COLORS = {
  open: "var(--zc-accent)",
  investigating: "var(--zc-warn,#f59e0b)",
  resolved: "var(--zc-success)",
  closed: "var(--zc-text-3)",
};

const CATEGORY_LABELS = {
  damage: "Damage",
  payment_refund: "Payment / refund",
  no_show: "No-show",
  fraud: "Fraud",
  other: "Other",
};

export default function ProfileSupport() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/support/cases/me`, { withCredentials: true });
      setCases(pageItems(r.data));
    } catch {
      setCases([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  return (
    <ProfileLayout eyebrow="Profile" title="Support cases">
      <div className="zc-card overflow-hidden" data-testid="profile-support-list">
        {loading && (
          <div className="px-6 py-10 text-center text-[var(--zc-text-3)] text-sm">Loading…</div>
        )}
        {!loading && cases.length === 0 && (
          <div className="px-6 py-12 text-center text-[var(--zc-text-3)] text-sm">
            <LifeBuoy size={28} className="mx-auto mb-3 opacity-50" />
            No support cases yet. Report an issue from a booking if something went wrong.
            <div className="mt-4">
              <Link to="/profile/bookings" className="zc-btn-primary px-4 h-9 inline-grid place-items-center text-xs">
                My bookings
              </Link>
            </div>
          </div>
        )}
        {cases.map((c) => (
          <div
            key={c.case_id}
            className="px-4 sm:px-6 py-4 border-b border-[var(--zc-border)] last:border-0"
            data-testid={`support-case-${c.case_id}`}
          >
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="min-w-0">
                <div className="font-semibold text-sm">{c.subject}</div>
                <div className="text-xs text-[var(--zc-text-3)] mt-1">
                  {CATEGORY_LABELS[c.category] || c.category}
                  {c.booking_id && (
                    <> · Booking <span className="font-mono">{c.booking_id}</span></>
                  )}
                </div>
              </div>
              <span
                className="text-xs font-semibold px-2 py-1 rounded-full capitalize"
                style={{
                  background: "var(--zc-bg-3)",
                  color: STATUS_COLORS[c.status] || "var(--zc-text-2)",
                }}
              >
                {(c.status || "open").replace("_", " ")}
              </span>
            </div>
            <p className="text-sm text-[var(--zc-text-2)] mt-2 line-clamp-3">{c.body}</p>
            <div className="text-xs text-[var(--zc-text-3)] mt-2">
              Filed {formatLocalTimestamp(c.created_at) || c.created_at}
            </div>
          </div>
        ))}
      </div>
    </ProfileLayout>
  );
}
